/**
 * IngestHealth — ops view of the parsedmarc → Postgres/OpenSearch pipeline.
 *
 * Backed by `GET /system/ingest-health`. When that endpoint returns a
 * 404 we render a friendly explanatory EmptyState
 * instead of breaking.
 */

import { useQuery } from '@tanstack/react-query';
import { Activity, Inbox } from 'lucide-react';

import { apiGet, ApiError } from '../../lib/api';
import { formatNumber, formatPercent, formatRelativeTime } from '../../lib/locale';
import { Card, CardHeader } from '../../components/Card';
import { KPICard } from '../../components/KPICard';
import { EmptyState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';

// ---------------------------------------------------------------------------
// Types (loosely mirror future backend schema; tolerant of partial payloads)
// ---------------------------------------------------------------------------

interface IngestHealthOut {
  messages_last_hour: number | null;
  parse_success_rate: number | null;
  unmatched_count: number | null;
  queue_depth: number | null;
  recent_unmatched: UnmatchedFile[];
}

interface UnmatchedFile {
  filename: string;
  received_at: string;
  reason: string | null;
  size_bytes: number | null;
}

// ---------------------------------------------------------------------------

export function IngestHealth(): JSX.Element {
  const query = useQuery({
    queryKey: ['admin', 'ingest-health'],
    queryFn: () => apiGet<{ data: IngestHealthOut }>('/system/ingest-health'),
    retry: false,
  });

  const is404 = query.error instanceof ApiError && query.error.status === 404;

  if (query.isLoading) {
    return (
      <div className="flex justify-center px-6 py-12">
        <Spinner />
      </div>
    );
  }

  if (is404) {
    return (
      <div className="px-6 py-5">
        <Header />
        <Card className="mt-4">
          <EmptyState
            icon={<Activity size={24} aria-hidden />}
            title="Geen ingest-statistieken beschikbaar"
            description="Ingest-statistieken zijn nog niet beschikbaar. Prometheus toont de teller-output direct via `parsedmarc_messages_total` en `parsedmarc_parse_seconds` op /metrics."
          />
        </Card>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="px-6 py-5">
        <Header />
        <Card className="mt-4">
          <EmptyState
            title="Kon ingest-status niet ophalen"
            description={query.error instanceof Error ? query.error.message : 'Onbekende fout.'}
          />
        </Card>
      </div>
    );
  }

  const health = query.data!.data;

  return (
    <div className="space-y-4 px-6 py-5">
      <Header />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Berichten (laatste uur)"
          value={health.messages_last_hour != null ? formatNumber(health.messages_last_hour) : '—'}
          sub="Aankomende RUA + RUF rapporten"
        />
        <KPICard
          label="Parse-success rate"
          value={health.parse_success_rate != null ? formatPercent(health.parse_success_rate) : '—'}
          sub="Aandeel succesvol geparsete payloads"
          accent={
            health.parse_success_rate != null && health.parse_success_rate < 0.95
              ? 'text-amber-700'
              : undefined
          }
        />
        <KPICard
          label="Unmatched bestanden"
          value={health.unmatched_count != null ? formatNumber(health.unmatched_count) : '—'}
          sub="Geen tenant-slug match (resolver)"
          accent={
            health.unmatched_count != null && health.unmatched_count > 0
              ? 'text-amber-700'
              : undefined
          }
        />
        <KPICard
          label="Dramatiq queue diepte"
          value={health.queue_depth != null ? formatNumber(health.queue_depth) : '—'}
          sub="Pending taken voor ingest+enrichment"
        />
      </div>

      <Card>
        <CardHeader
          title="Recente unmatched mailbox-bestanden"
          subtitle="Laatste 10 RUA-rapporten die niet aan een tenant gekoppeld konden worden."
        />
        {health.recent_unmatched.length === 0 ? (
          <EmptyState
            icon={<Inbox size={20} aria-hidden />}
            title="Niks unmatched"
            description="Alle inkomende RUA-rapporten konden aan een tenant worden gekoppeld."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Bestand</th>
                  <th className="px-3 py-2">Ontvangen</th>
                  <th className="px-3 py-2">Reden</th>
                  <th className="px-3 py-2 text-right">Grootte</th>
                </tr>
              </thead>
              <tbody>
                {health.recent_unmatched.slice(0, 10).map((file) => (
                  <tr key={file.filename} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-mono text-slate-900">{file.filename}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">
                      {formatRelativeTime(file.received_at)}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{file.reason ?? 'Onbekend'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {file.size_bytes != null ? `${formatNumber(file.size_bytes)} B` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Header(): JSX.Element {
  return (
    <header>
      <h1 className="text-lg font-semibold text-slate-900">Ingest-pipeline</h1>
      <p className="mt-0.5 text-[12px] text-slate-500">
        Status van Postfix → ingest-worker → parsedmarc → Postgres/OpenSearch.
      </p>
    </header>
  );
}
